import React from 'react';
import styled from 'styled-components';
import { FiAlertCircle, FiRefreshCw, FiHome, FiDownload } from 'react-icons/fi';
import logger from '../../utils/logger';

// Utility to check if error is a chunk loading error (common after deployments)
const isChunkLoadError = (error) => {
  if (!error) return false;
  const errorMessage = error.message || error.toString();

  // Check for various chunk loading error patterns
  const chunkErrorPatterns = [
    /loading chunk/i,
    /Loading chunk/i,
    /ChunkLoadError/i,
    /failed to fetch dynamically imported module/i,
    /Importing a module script failed/i,
    /Unable to preload CSS/i,
    /Failed to load resource.*\.js/i,
    /Failed to load resource.*\.css/i,
    /Unexpected token '<'/i, // HTML returned instead of JS (old chunk not found)
    /SyntaxError: Unexpected token/i,
  ];

  return chunkErrorPatterns.some(pattern => pattern.test(errorMessage));
};

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
  background: linear-gradient(135deg, var(--accent-color) 0%, var(--contrast-accent) 100%);
  color: var(--text-inverse);
`;

const ErrorContent = styled.div`
  max-width: 600px;
  text-align: center;
  background: var(--background-secondary);
  opacity: 0.9;
  padding: 3rem;
  border-radius: 0;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
`;

const ErrorIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1.5rem;
  color: var(--error-color);
`;

const ErrorTitle = styled.h1`
  font-size: 2rem;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const ErrorMessage = styled.p`
  font-size: 1.1rem;
  margin-bottom: 2rem;
  opacity: 0.9;
  line-height: 1.6;
`;

const ErrorDetails = styled.details`
  margin: 2rem 0;
  text-align: left;
  background: rgba(0, 0, 0, 0.2);
  padding: 1rem;
  border-radius: 0;
  cursor: pointer;
  
  summary {
    font-weight: 600;
    margin-bottom: 0.5rem;
    cursor: pointer;
    user-select: none;
  }
  
  pre {
    font-size: 0.85rem;
    overflow-x: auto;
    white-space: pre-wrap;
    word-wrap: break-word;
    margin-top: 0.5rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  border-radius: 0;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.primary ? 'var(--text-inverse)' : 'var(--background-secondary)'};
  opacity: ${props => props.primary ? '1' : '0.8'};
  color: ${props => props.primary ? 'var(--accent-color)' : 'var(--text-inverse)'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`;

// Update required container (special styling for chunk errors)
const UpdateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
  background: linear-gradient(135deg, var(--warning-color) 0%, var(--warning-dark) 100%);
  color: var(--text-inverse);
`;

const UpdateContent = styled.div`
  max-width: 500px;
  text-align: center;
  background: var(--background-primary);
  padding: 3rem;
  border-radius: 0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`;

const UpdateIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1.5rem;
  color: var(--warning-color);
`;

const UpdateTitle = styled.h1`
  font-size: 1.75rem;
  margin-bottom: 1rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const UpdateMessage = styled.p`
  font-size: 1rem;
  margin-bottom: 2rem;
  color: var(--text-secondary);
  line-height: 1.6;
`;

const CountdownText = styled.p`
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin-top: 1rem;
`;

const UpdateButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border: none;
  border-radius: 0;
  cursor: pointer;
  transition: all 0.2s;
  background: var(--warning-color);
  color: var(--text-inverse);
  width: 100%;

  &:hover {
    background: var(--warning-dark);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
      isChunkError: false,
      autoRefreshCountdown: 10 // Seconds until auto-refresh for chunk errors
    };
    this.countdownInterval = null;
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    // Also check if it's a chunk loading error
    return {
      hasError: true,
      isChunkError: isChunkLoadError(error)
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error
    logger.error('Error caught by boundary', { error, errorInfo });

    const isChunkErr = isChunkLoadError(error);

    // Log to error tracking service (if available)
    if (window.Sentry || window.Rollbar) {
      const errorService = window.Sentry || window.Rollbar;
      errorService.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack
          }
        },
        tags: {
          isChunkError: isChunkErr
        }
      });
    }

    // Store error details in state
    this.setState({
      error,
      errorInfo,
      errorCount: this.state.errorCount + 1,
      isChunkError: isChunkErr
    });

    // For chunk errors, start auto-refresh countdown
    if (isChunkErr) {
      console.log('🔄 Chunk loading error detected - app update required');
      this.startAutoRefreshCountdown();
    } else {
      // Optionally send error to backend for logging (not for chunk errors)
      this.logErrorToBackend(error, errorInfo);
    }
  }

  componentWillUnmount() {
    // Clear countdown interval when component unmounts
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  startAutoRefreshCountdown = () => {
    // Clear any existing interval
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    // Start countdown
    this.countdownInterval = setInterval(() => {
      this.setState(prevState => {
        const newCountdown = prevState.autoRefreshCountdown - 1;
        if (newCountdown <= 0) {
          clearInterval(this.countdownInterval);
          // Force refresh with cache clear
          this.handleForceRefresh();
          return { autoRefreshCountdown: 0 };
        }
        return { autoRefreshCountdown: newCountdown };
      });
    }, 1000);
  };

  async logErrorToBackend(error, errorInfo) {
    try {
      const apiService = (await import('../../services/api')).default;
      // FIX: Remove /api prefix since baseURL already includes /portal/api
      await apiService.request('/log-error', {
        method: 'POST',
        body: JSON.stringify({
          error: {
            message: error.message,
            stack: error.stack,
            name: error.name
          },
          errorInfo: {
            componentStack: errorInfo.componentStack
          },
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href
        })
      });
    } catch (logError) {
      // Silently fail - don't break the error boundary
      logger.warn('Failed to log error to backend', { error: logError });
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleReload = () => {
    window.location.reload();
  };

  handleForceRefresh = async () => {
    console.log('🔄 Force refreshing app to get latest version...');

    try {
      // Clear all caches if available
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log('✅ Browser caches cleared');
      }

      // Unregister service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map(registration => registration.unregister())
        );
        console.log('✅ Service workers unregistered');
      }
    } catch (error) {
      console.warn('Cache clearing failed, proceeding with reload:', error);
    }

    // Force reload without cache
    window.location.reload(true);
  };

  render() {
    if (this.state.hasError) {
      // Special UI for chunk loading errors (app update required)
      if (this.state.isChunkError) {
        return (
          <UpdateContainer>
            <UpdateContent>
              <UpdateIcon>
                <FiDownload />
              </UpdateIcon>
              <UpdateTitle>App Update Required</UpdateTitle>
              <UpdateMessage>
                A new version of the application has been deployed. Please refresh to get the latest
                version with new features and bug fixes.
              </UpdateMessage>

              <UpdateButton onClick={this.handleForceRefresh}>
                <FiRefreshCw />
                Refresh Now
              </UpdateButton>

              <CountdownText>
                Auto-refreshing in {this.state.autoRefreshCountdown} seconds...
              </CountdownText>
            </UpdateContent>
          </UpdateContainer>
        );
      }

      // Custom fallback UI for other errors
      return (
        <ErrorContainer>
          <ErrorContent>
            <ErrorIcon>
              <FiAlertCircle />
            </ErrorIcon>
            <ErrorTitle>Something went wrong</ErrorTitle>
            <ErrorMessage>
              We're sorry, but something unexpected happened. Don't worry, your data is safe.
              {this.state.errorCount > 1 && (
                <span> This error has occurred {this.state.errorCount} times.</span>
              )}
            </ErrorMessage>

            {this.state.error && process.env.NODE_ENV === 'development' && (
              <ErrorDetails>
                <summary>Error Details (Development Only)</summary>
                <pre>{this.state.error.toString()}</pre>
                {this.state.errorInfo && (
                  <pre>{this.state.errorInfo.componentStack}</pre>
                )}
              </ErrorDetails>
            )}

            <ButtonGroup>
              <Button onClick={this.handleReset}>
                <FiRefreshCw />
                Try Again
              </Button>
              <Button primary onClick={this.handleGoHome}>
                <FiHome />
                Go Home
              </Button>
              <Button onClick={this.handleReload}>
                <FiRefreshCw />
                Reload Page
              </Button>
            </ButtonGroup>
          </ErrorContent>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
